import React from 'react';
import {
  BarChart3, Eye, TrendingUp, Shield, Clock,
  Target, Settings, AlertTriangle,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const AssetPerformancePage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Asset Performance',
      title: (
        <>
          Monitor and assess <em>renewable asset portfolios</em>
        </>
      ),
      lede: 'Monitor and assess the health of renewable asset portfolios in one view, highlight which assets need technical attention with our comprehensive performance monitoring solution.',
      image: '/assets/img/general/steptodown.com799996.jpg',
      imageAlt: 'Renewable energy assets in operation',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '99.9%', label: 'Uptime monitoring' },
        { value: '24/7', label: 'Real-time alerts' },
        { value: '500+', label: 'Assets monitored' },
        { value: '<1min', label: 'Response time' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Advanced monitoring features',
      lede: 'Our comprehensive asset performance monitoring solution provides everything you need to optimize your renewable energy operations and maximize asset value.',
      body: [
        'Comprehensive monitoring dashboard with live data visualization and performance metrics.',
      ],
      checklist: [
        'Real-time performance monitoring across all renewable assets',
        'Advanced analytics and reporting dashboards',
        'Automated alert systems for maintenance and performance issues',
        'Predictive maintenance scheduling and optimization',
        'Comprehensive health assessment and diagnostics',
        'Integration with existing SCADA and monitoring systems',
        'Customizable reporting and data visualization',
        'Mobile access for field technicians and operators',
      ],
      image: '/assets/img/general/photovoltaic-4525178_1280.jpg',
      imageAlt: 'Photovoltaic assets under continuous assessment',
      imageCaption: 'Live data visualization and performance metrics in one dashboard',
    }}
    capabilities={{
      eyebrow: 'Monitoring solutions',
      title: 'Comprehensive monitoring solutions',
      lede: 'Advanced monitoring capabilities to ensure optimal performance of your renewable energy assets.',
      items: [
        {
          icon: Eye,
          title: 'Real-time monitoring',
          description: 'Continuous surveillance of asset performance with instant alerts and notifications.',
        },
        {
          icon: BarChart3,
          title: 'Performance analytics',
          description: 'Advanced analytics to track efficiency, output, and operational metrics.',
        },
        {
          icon: AlertTriangle,
          title: 'Health assessment',
          description: 'Comprehensive health monitoring to identify potential issues before they impact performance.',
        },
        {
          icon: Settings,
          title: 'Technical alerts',
          description: 'Automated alerts for maintenance needs, performance degradation, and system anomalies.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Benefits',
      title: 'Benefits of asset performance monitoring',
      lede: 'Discover how our advanced monitoring solutions can transform your renewable energy operations.',
      items: [
        {
          icon: TrendingUp,
          title: 'Increased efficiency',
          description: 'Optimize energy generation and reduce operational costs through advanced monitoring.',
        },
        {
          icon: Shield,
          title: 'Enhanced reliability',
          description: 'Minimize downtime and ensure consistent performance with predictive maintenance.',
        },
        {
          icon: Clock,
          title: '24/7 surveillance',
          description: 'Round-the-clock monitoring with automated alerts for immediate response.',
        },
        {
          icon: Target,
          title: 'Proactive maintenance',
          description: 'Identify issues before they become problems with predictive analytics.',
        },
      ],
    }}
    cta={{
      title: 'Ready to optimize your asset performance?',
      body: 'Get started with our comprehensive asset performance monitoring solution today.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default AssetPerformancePage;
